import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useToastStore } from '@/store/toastStore'
import { trackEngagementEvent } from '@/services/analytics'

/**
 * Prevents the browser back button from navigating away.
 * Shows a toast message when the user tries to go back.
 *
 * Strategy:
 * 1. Push many forward entries on mount to create a large buffer
 * 2. On every popstate (back pressed), immediately push more entries
 * 3. Use RAF to handle rapid back presses more reliably
 * 4. Show toast with debounce to prevent stacking
 *
 * @param message - The message to show in the toast
 */
export function useBackPrevent(message: string) {
  const showToast = useToastStore((state) => state.showToast)
  const location = useLocation()
  const lastToastTime = useRef(0)
  const mountedRef = useRef(true)

  // Memoize the restore function to push multiple entries
  const restoreHistory = useCallback((url: string) => {
    // Push multiple entries to rebuild the buffer
    for (let i = 0; i < 5; i++) {
      window.history.pushState({ preventBack: true, index: i, time: Date.now() }, '', url)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const currentPath = location.pathname
    const currentUrl = window.location.href

    // Push many forward entries to create a large buffer against rapid back presses
    for (let i = 0; i < 10; i++) {
      window.history.pushState({ preventBack: true, index: i }, '', currentUrl)
    }

    const handlePopState = () => {
      if (!mountedRef.current) return

      // Use RAF to ensure we're not in the middle of a navigation
      requestAnimationFrame(() => {
        if (!mountedRef.current) return

        // Aggressively restore the history buffer
        restoreHistory(currentUrl)

        // Debounce: only show toast if more than 2 seconds since last one
        const now = Date.now()
        if (now - lastToastTime.current > 2000) {
          lastToastTime.current = now
          showToast(message, 'warning')
          trackEngagementEvent.backPrevented(currentPath)
        }
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      mountedRef.current = false
      window.removeEventListener('popstate', handlePopState)
    }
  }, [location.pathname, message, showToast, restoreHistory])
}
